
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import type { ShelterType as Shelter, UserType as User } from '../../utils/supabase';

export default function ShelterDetail() {
  const { id } = useParams();
  const [shelter, setShelter] = useState<Shelter | null>(null);
  const [owner, setOwner] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showDonationModal, setShowDonationModal] = useState(false);

  // Cargar datos del refugio
  useEffect(() => {
    if (id) {
      loadShelterData();
    }
  }, [id]);

  const loadShelterData = async () => {
    try {
      // Cargar refugio
      const { data: shelterData } = await supabase
        .from('shelters')
        .select('*')
        .eq('id', id)
        .single();

      if (shelterData) {
        setShelter(shelterData);

        // Cargar datos del propietario
        const { data: userData } = await supabase
          .from('users')
          .select('*')
          .eq('id', shelterData.user_id)
          .single();

        setOwner(userData);
      }
    } catch (error) {
      console.error('Error cargando refugio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCall = () => {
    const phoneNumber = shelter?.contact_phone || owner?.phone;
    if (phoneNumber) {
      window.open(`tel:${phoneNumber}`, '_self');
    } else {
      alert('Número de teléfono no disponible');
    }
  };

  const handleWhatsApp = () => {
    if (shelter?.whatsapp_link) {
      // Si el refugio tiene WhatsApp configurado, usar ese enlace
      let whatsappUrl = '';
      
      if (shelter.whatsapp_link.includes('wa.me') || shelter.whatsapp_link.includes('whatsapp.com')) {
        // Si ya es un enlace completo, verificar si tiene mensaje
        if (shelter.whatsapp_link.includes('text=')) {
          whatsappUrl = shelter.whatsapp_link;
        } else {
          // Agregar el mensaje al enlace existente
          const message = `Hola, me interesa conocer más sobre el refugio "${shelter?.name}" y cómo puedo ayudar`;
          const separator = shelter.whatsapp_link.includes('?') ? '&' : '?';
          whatsappUrl = `${shelter.whatsapp_link}${separator}text=${encodeURIComponent(message)}`;
        }
      } else {
        // Si es solo un número, crear el enlace completo
        const cleanNumber = shelter.whatsapp_link.replace(/[^0-9]/g, '');
        const message = `Hola, me interesa conocer más sobre el refugio "${shelter?.name}" y cómo puedo ayudar`;
        whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
      }
      
      window.open(whatsappUrl, '_blank');
    } else if (owner?.phone) {
      // Fallback al teléfono del propietario
      const message = `Hola, me interesa conocer más sobre el refugio "${shelter?.name}" y cómo puedo ayudar`;
      const whatsappUrl = `https://wa.me/${owner.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } else {
      alert('WhatsApp no disponible');
    }
  };

  const handleFacebook = () => {
    if (shelter?.facebook_link) {
      window.open(shelter.facebook_link, '_blank');
    } else {
      alert('Red social no encontrada');
    }
  };

  const handleInstagram = () => {
    if (shelter?.instagram_link) {
      window.open(shelter.instagram_link, '_blank');
    } else {
      alert('Red social no encontrada');
    }
  };

  const handleDonate = () => {
    if (shelter?.donation_bank_info) {
      // Mostrar modal con información bancaria
      setShowDonationModal(true);
    } else {
      alert('Información de donación no disponible. Contacta al refugio directamente.');
    }
  };

  const handleVolunteer = () => {
    // Aquí se implementaría la lógica para ser voluntario
    alert('Funcionalidad de voluntariado en desarrollo');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: shelter?.name,
        text: `Conoce el refugio ${shelter?.name} - ${shelter?.description}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Enlace copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando refugio...</p>
        </div>
      </div>
    );
  }

  if (!shelter) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Refugio no encontrado</h2>
          <p className="text-gray-600 mb-6">El refugio que buscas no existe o ha sido eliminado.</p>
          <button 
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  // Imágenes de galería del refugio
  const galleryImages = [
    shelter.image_url || 'https://readdy.ai/api/search-image?query=Modern%20professional%20animal%20shelter%20facility%20exterior%20view%2C%20clean%20contemporary%20building%20design%20for%20dog%20rescue%20center%2C%20welcoming%20entrance%20with%20natural%20lighting%2C%20professional%20architecture%20photography%20with%20clear%20blue%20sky%20background&width=800&height=600&seq=shelter-main&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Interior%20view%20of%20clean%20modern%20dog%20shelter%20facility%2C%20spacious%20kennels%20with%20comfortable%20bedding%2C%20bright%20natural%20lighting%2C%20professional%20veterinary%20care%20environment%2C%20dogs%20resting%20peacefully%20in%20clean%20organized%20space&width=800&height=600&seq=shelter-interior1&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Dog%20play%20area%20in%20animal%20shelter%2C%20happy%20rescued%20dogs%20playing%20together%20in%20secure%20outdoor%20yard%2C%20green%20grass%20and%20safe%20fencing%2C%20volunteers%20interacting%20with%20pets%20in%20caring%20environment&width=800&height=600&seq=shelter-play&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Veterinary%20medical%20room%20in%20animal%20shelter%2C%20professional%20medical%20equipment%20for%20pet%20care%2C%20clean%20sterile%20environment%2C%20caring%20veterinarian%20examining%20rescued%20dog%20with%20compassionate%20care&width=800&height=600&seq=shelter-medical&orientation=landscape'
  ];

  const occupancyPercentage = (shelter.current_dogs / shelter.capacity) * 100;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Volver</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <img 
                src="/images/image.png" 
                alt="Huellitas Unidas Logo" 
                className="w-8 h-8 object-contain"
              />
              <h1 className="text-lg font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                Huellitas Unidas
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <button 
                onClick={handleShare}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-share-line text-xl text-gray-600"></i>
              </button>
              <button className="p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                <i className="ri-heart-line text-xl text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Gallery Section */}
        <div className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-96 lg:h-[500px]">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src={galleryImages[activeImageIndex]}
                alt={shelter.name}
                className="w-full h-full object-cover object-top cursor-pointer"
                onClick={() => setActiveImageIndex(0)}
              />
              <div className="absolute top-4 left-4 bg-green-500 text-white px-4 py-2 rounded-full font-semibold">
                <i className="ri-building-fill mr-2"></i>
                REFUGIO CERTIFICADO
              </div>
            </div>
            
            {/* Thumbnail Grid */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {galleryImages.slice(1, 5).map((image, index) => (
                <div 
                  key={index + 1}
                  className="relative rounded-2xl overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setActiveImageIndex(index + 1)}
                >
                  <img 
                    src={image}
                    alt={`${shelter.name} ${index + 2}`}
                    className="w-full h-full object-cover object-top"
                  />
                  {index === 3 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-semibold">Ver todas las fotos</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Gallery Dots */}
          <div className="flex justify-center space-x-2 mt-4 lg:hidden">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                  activeImageIndex === index ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title and Basic Info */}
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                {shelter.name}
              </h1>
              
              <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
                <div className="flex items-center space-x-2">
                  <i className="ri-map-pin-fill text-red-500"></i>
                  <span>{shelter.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-user-fill text-blue-500"></i>
                  <span>Contacto: {shelter.contact}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-calendar-fill text-green-500"></i>
                  <span>Activo desde {new Date(shelter.created_at).getFullYear()}</span>
                </div>
              </div>

              {/* Capacity Info */}
              <div className="bg-gray-100 rounded-2xl p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">
                      {shelter.current_dogs}
                    </div>
                    <div className="text-gray-600">perritos actualmente</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-500">
                      {shelter.capacity}
                    </div>
                    <div className="text-gray-600">capacidad máxima</div>
                  </div>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                  <div 
                    className="bg-gradient-to-r from-green-400 to-green-600 h-4 rounded-full transition-all duration-1000"
                    style={{ width: `${Math.min(occupancyPercentage, 100)}%` }}
                  ></div>
                </div>
                
                <div className="text-sm text-gray-600 text-center">
                  {occupancyPercentage >= 100 ? 'Refugio a capacidad máxima' : `Espacio disponible para ${shelter.capacity - shelter.current_dogs} perritos más`}
                </div>
              </div>
            </div>

            {/* Story Section */}
            <div className="bg-white">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-building-fill text-green-500 mr-3"></i>
                Nuestra Historia
              </h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="mb-6">{shelter.description}</p>
                
                <div className="bg-green-50 rounded-2xl p-6 mb-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-4">¿Cómo puedes ayudar?</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-700">
                    <div className="flex items-center space-x-2">
                      <i className="ri-heart-fill text-green-500"></i>
                      <span>Donaciones monetarias</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-hand-heart-fill text-green-500"></i>
                      <span>Voluntariado</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-gift-fill text-green-500"></i>
                      <span>Donaciones de alimento</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-home-heart-fill text-green-500"></i>
                      <span>Adopciones responsables</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Owner Info */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-user-line text-blue-500 mr-3"></i>
                Información del Responsable
              </h3>
              
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {owner?.shelter_name?.charAt(0) || 'R'}
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {owner?.shelter_name}
                  </h4>
                  <div className="space-y-2 text-gray-600">
                    <div className="flex items-center space-x-2">
                      <i className="ri-map-pin-fill text-red-500"></i>
                      <span>{owner?.city}, {owner?.state}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-phone-fill text-green-500"></i>
                      <span>{owner?.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-calendar-fill text-blue-500"></i>
                      <span>Miembro desde {new Date(owner?.created_at || '').getFullYear()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-vip-crown-fill text-yellow-500"></i>
                      <span>Plan {owner?.plan}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Contact & Actions */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {shelter.current_dogs}/{shelter.capacity}
                  </div>
                  <div className="text-gray-600">perritos en el refugio</div>
                </div>

                <div className="space-y-4 mb-8">
                  <button 
                    onClick={handleDonate}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-heart-fill mr-2"></i>
                    Donar al Refugio
                  </button>
                  
                  <button 
                    onClick={handleVolunteer}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-hand-heart-fill mr-2"></i>
                    Ser Voluntario
                  </button>

                  <div className="grid grid-cols-2 gap-3">
                    <button 
                      onClick={handleCall}
                      className="flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-fill"></i>
                      <span>Llamar</span>
                    </button>
                    
                    <button 
                      onClick={handleWhatsApp}
                      className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill"></i>
                      <span>WhatsApp</span>
                    </button>
                  </div>

                  {/* Redes Sociales - Nueva sección */}
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <button 
                      onClick={handleFacebook}
                      className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-facebook-fill"></i>
                      <span>Facebook</span>
                    </button>
                    
                    <button 
                      onClick={handleInstagram}
                      className="flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-instagram-fill"></i>
                      <span>Instagram</span>
                    </button>
                  </div>

                  {/* Botón de Eventos Próximos - Nueva sección */}
                  <div className="mt-4">
                    <button 
                      onClick={() => window.REACT_APP_NAVIGATE(`/shelter/${shelter.id}/events`)}
                      className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-calendar-event-fill"></i>
                      <span>Eventos Próximos</span>
                    </button>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="text-center text-sm text-gray-600 mb-4">
                    <i className="ri-shield-check-fill text-green-500 mr-1"></i>
                    Refugio verificado y certificado
                  </div>
                  
                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-center justify-between">
                      <span>Capacidad:</span>
                      <span className="font-semibold">{shelter.capacity} perritos</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Ocupación:</span>
                      <span className="font-semibold text-green-500">{Math.round(occupancyPercentage)}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Disponibles:</span>
                      <span className="font-semibold text-blue-500">{shelter.capacity - shelter.current_dogs} espacios</span>
                    </div>
                    {(shelter?.contact_phone || owner?.phone) && (
                      <div className="flex items-center justify-between">
                        <span>Teléfono:</span>
                        <span className="font-semibold text-gray-700">{shelter?.contact_phone || owner?.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-6 bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Acciones rápidas</h3>
                <div className="space-y-3">
                  <button 
                    onClick={handleShare}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer"
                  >
                    <i className="ri-share-fill text-blue-500"></i>
                    <span className="text-gray-700">Compartir refugio</span>
                  </button>
                  <button 
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer"
                  >
                    <i className="ri-home-heart-fill text-purple-500"></i>
                    <span className="text-gray-700">Ver adopciones</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer">
                    <i className="ri-building-fill text-green-500"></i>
                    <span className="text-gray-700">Otros refugios</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Donación */}
      {showDonationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-fill text-2xl text-green-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Donar al Refugio {shelter?.name}
              </h3>
              <p className="text-gray-600">Información bancaria para donaciones</p>
            </div>

            <div className="mb-6">
              <div className="bg-green-50 rounded-2xl p-4">
                <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center">
                  <i className="ri-bank-fill mr-2"></i>
                  Información Bancaria
                </h4>
                <div className="text-sm text-green-700 whitespace-pre-line">{shelter?.donation_bank_info}</div>
                <button
                  onClick={() => {
                    if (shelter?.donation_bank_info) {
                      navigator.clipboard.writeText(shelter.donation_bank_info);
                      alert('Información copiada al portapapeles');
                    }
                  }}
                  className="mt-2 text-xs bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-file-copy-line mr-1"></i>
                  Copiar información
                </button>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="text-center text-sm text-gray-600 mb-4">
                <i className="ri-shield-check-fill text-green-500 mr-1"></i>
                Donación directa al refugio
              </div>

              <button
                onClick={() => setShowDonationModal(false)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
